import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';

import { EndpointsService } from '@app/core/services/endpoints/endpoints.service';
import { ApiService, HttpError } from '@app/core/services/api/api.service';

import { catchError, map } from 'rxjs/operators';
import {
  PagedQuicklinksSet,
  QuicklinksSet,
  QuicklinksSetRequest} from '../models/quicklinks-set.model';
import { Sort } from '@rds/angular-components';
import { format } from 'date-fns';


@Injectable()
export class QuicklinksSetService {

  public putFilterQuicklinksSet(
    {sort, filters, pageIndex, pageSize}: {
      sort: Sort,
      filters: { [key: string]: any },
      pageIndex: number,
      pageSize: number
    }
  ): Observable<PagedQuicklinksSet>  {
    const url = this.endpoints.ENDPOINT.QUICKLINKS_SET.FILTER.PUT;
    const body = {
      departments: filters.departments,
      functions: filters.functions,
      locations: filters.locations,
      name: filters.search?.trim(),
      modifiedBy: filters.modifiedBy?.map(u => u.identifier),
      modifiedDate: filters.modifiedDate ? format(filters.modifiedDate,'yyy-MM-dd') : null,
      catalogs: filters.catalogs?.map(c => c.catalogId),
      order: `${sort.active} ${sort.direction}`
    };
    return this.http
      .put(this.endpoints.addParams(url, {pageIndex, pageSize}), body)
      .pipe(
        map((res) => ({
          data: res.data.map(qs => ({
            ...qs,
            locations: filters.locations.length ? qs.locations.sort((a, b) => filters.locations.indexOf(b) - filters.locations.indexOf(a)) : qs.locations,
            departments: filters.departments.length ? qs.departments.sort((a, b) => filters.departments.indexOf(b) - filters.departments.indexOf(a)) : qs.departments,
            functions: filters.functions.length ? qs.functions.sort((a, b) => filters.functions.indexOf(b) - filters.functions.indexOf(a)) : qs.functions,
          })),
          pagination: {
            isFirst: res.isFirst,
            isLast: res.isLast,
            pageCount: res.pageCount,
            pageIndex: res.pageIndex,
            pageSize: res.pageSize,
            totalCount: res.totalCount,
          }
        })),
        catchError((error: HttpError) => throwError(`Cannot get quicklinks sets. ${error.message}`))
      );
  }

  public getFullQuicklinksSet(id: number): Observable<QuicklinksSet> {
    const url = this.endpoints.replaceUrlTokens(
      this.endpoints.ENDPOINT.QUICKLINKS_SET.GET, { id }
    );
    return this.http
      .get(url)
      .pipe(
        catchError((error: HttpError) => throwError(`Cannot get quicklinks sets. ${error.message}`))
      );
  }

  public putQuicklinksSet(body: QuicklinksSetRequest): Observable<any> {
    const url = this.endpoints.ENDPOINT.QUICKLINKS_SET.PUT;
    return this.http
      .put(url, body)
      .pipe(
        catchError((error: HttpError) => throwError(`Cannot get quicklinks sets. ${error.message}`))
      );
  }

  public putQuicklinksSetSearch(value: string): Observable<any> {
    const url = this.endpoints.ENDPOINT.QUICKLINKS_SET.SEARCH.PUT + `?searchProperty=name&searchPhrase=` + value;

    return this.http
      .put(url, {})
      .pipe(
        catchError((error: HttpError) => throwError(`Cannot get quicklinks sets. ${error.message}`))
      );
  }

  public postQuicklinksSet(body: QuicklinksSetRequest): Observable<any> {
    const url = this.endpoints.ENDPOINT.QUICKLINKS_SET.POST;
    return this.http
      .post(url, body)
      .pipe(
        catchError((error: HttpError) => throwError(`Cannot get quicklinks sets. ${error.message}`))
      );
  }

  public getIsNameUnique(name: string): Observable<boolean> {
    const url = this.endpoints.ENDPOINT.QUICKLINKS_SET.UNIQUE.GET;
    const params = { name };
    return this.http
      .get(this.endpoints.addParams(url, params))
      .pipe(
        catchError((error: HttpError) => throwError(`Cannot get quicklinks sets. ${error.message}`))
      );
  }

  public deleteQuicklinksSet(id: string | number): Observable<boolean> {
    const url = this.endpoints.replaceUrlTokens(
      this.endpoints.ENDPOINT.QUICKLINKS_SET.DELETE, { id }
    );
    return this.http
      .delete(url)
      .pipe(
        catchError((error: HttpError) => throwError(`Cannot get quicklinks sets. ${error.message}`))
      );
  }

  constructor(
    private http: ApiService,
    private endpoints: EndpointsService,
  ) {}

}
